import { useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AddRestaurantForm } from "@/components/add-restaurant-form";
import { getRestaurants, updateRestaurant, removeRestaurant } from "@/lib/store";
import type { Restaurant } from "@/types";
import { deriveTasteFields } from "@/lib/rating";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ImagePlus, Camera, Image, Trash2 } from "lucide-react";

const EditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const current = useMemo<Restaurant | undefined>(() => {
    if (!id) return undefined;
    const list = getRestaurants();
    // try exact match, then legacy prefix match (e.g., id like "<ts>-xxxx")
    return list.find((r) => r.id === id) || list.find((r) => r.id?.startsWith?.(id));
  }, [id]);

  if (!id || !current) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">編集対象が見つかりません</h1>
          <p className="text-muted-foreground">URLのIDが無効、またはデータが削除されています</p>
          <Button onClick={() => navigate('/')}>一覧へ戻る</Button>
        </div>
      </div>
    );
  }

  const handleSubmit = (data: any) => {
    const tastePatch = deriveTasteFields(data as any);
    const ratingTasteValue = typeof tastePatch.ratingTaste === 'number'
      ? tastePatch.ratingTaste
      : (typeof data.ratingTaste === 'number' ? data.ratingTaste : 0);
    const scoreValues = [ratingTasteValue, data.ratingAmbience, data.ratingService, data.ratingCost, data.ratingTasteBalance, data.ratingTasteIngredients, data.ratingTasteTechnique]
      .map((v: any) => (typeof v === 'number' && v > 0 ? v : 0));
    const hasVisitEvidence = scoreValues.some((v) => v > 0) || !!data.visitDate || (Array.isArray(data.visitHistory) && data.visitHistory.length > 0);

    updateRestaurant(id, {
      name: data.name,
      cuisine: data.cuisine,
      location: data.location,
      prefecture: data.prefecture,
      municipality: data.municipality,
      rating: data.rating,
      ratingTaste: ratingTasteValue,
      ratingTasteBalance: tastePatch.ratingTasteBalance ?? data.ratingTasteBalance,
      ratingTasteIngredients: tastePatch.ratingTasteIngredients ?? data.ratingTasteIngredients,
      ratingTasteTechnique: tastePatch.ratingTasteTechnique ?? data.ratingTasteTechnique,
      ratingTasteFine: tastePatch.ratingTasteFine ?? undefined,
      ratingTasteGrade: tastePatch.ratingTasteGrade ?? undefined,
      ratingAmbience: data.ratingAmbience,
      ratingService: data.ratingService,
      ratingCost: data.ratingCost,
      visitDate: data.visitDate,
      visitHistory: data.visitHistory,
      isWishlist: hasVisitEvidence ? false : !!data.isWishlist,
      notes: data.notes,
      image: data.image,
      priceRange: data.priceRange,
      sourceUrl: data.sourceUrl,
      tiktokUrl: data.tiktokUrl,
    });
    navigate("/");
  };

  async function uploadImage(file: File): Promise<string> {
    const fd = new FormData();
    fd.append('file', file);
    const resp = await fetch('/api/upload-image', { method: 'POST', body: fd });
    if (!resp.ok) throw new Error('アップロードに失敗しました');
    const data = await resp.json();
    return data.url as string;
  }

  async function onAddPhotos(files: FileList | null) {
    if (!files || files.length === 0 || !current) return;
    const max = 5;
    const existing = Array.isArray(current.images) ? current.images.slice() : [];
    const room = Math.max(0, max - existing.length);
    const targets = Array.from(files).slice(0, room);
    const urls: string[] = [];
    for (const f of targets) {
      try { const url = await uploadImage(f); urls.push(url); } catch {}
    }
    if (urls.length > 0) {
      updateRestaurant(id!, { images: existing.concat(urls) } as any);
      location.reload();
    }
  }

  function removeAt(index: number) {
    if (!current) return;
    const arr = Array.isArray(current.images) ? current.images.slice() : [];
    arr.splice(index, 1);
    updateRestaurant(id!, { images: arr } as any);
    location.reload();
  }

  async function replaceAt(index: number, file: File) {
    if (!current) return;
    try {
      const url = await uploadImage(file);
      const arr = Array.isArray(current.images) ? current.images.slice() : [];
      arr[index] = url;
      updateRestaurant(id!, { images: arr } as any);
      location.reload();
    } catch {}
  }

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="min-h-screen bg-background p-3 sm:p-6">
      <div className="container mx-auto max-w-3xl">
        <div className="flex justify-end mb-2 sm:mb-3">
          <Button
            variant="destructive"
            onClick={() => {
              if (confirm('このお店を削除します。よろしいですか？')) {
                removeRestaurant(id);
                navigate('/');
              }
            }}
          >
            削除する
          </Button>
        </div>
        <AddRestaurantForm
          mode="edit"
          initialData={current}
          onSubmit={handleSubmit}
          onCancel={() => navigate("/")}
        />

        {/* Photo Gallery (edit) */}
        <Card className="mt-3 border-0 shadow-card p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-muted-foreground">写真（最大5枚）</div>
            <div className="flex gap-2"><button className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-md border" onClick={() => cameraInputRef.current?.click()}>撮影する</button><button className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-md border" onClick={() => galleryInputRef.current?.click()}>フォトライブラリ</button></div>
            <input ref={cameraInputRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={(e)=> onAddPhotos(e.currentTarget.files)} />
            <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e)=> onAddPhotos(e.currentTarget.files)} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {(current.images || []).map((u, idx) => (
              <div key={u+idx} className="relative group">
                <img src={u} alt={`photo-${idx+1}`} className="w-full h-24 object-cover rounded" />
                <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/40 transition">
                  <label className="text-white text-xs px-2 py-1 rounded border border-white cursor-pointer">
                    差し替え
                    <input type="file" accept="image/*" className="hidden" onChange={(e)=> { const f=e.currentTarget.files?.[0]; if(f) replaceAt(idx, f); }} />
                  </label>
                  <button className="text-white text-xs px-2 py-1 rounded border border-white" onClick={()=> removeAt(idx)}>
                    <Trash2 className="inline h-3 w-3 mr-1"/>削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default EditPage;
